/*
 * Boston Proper × Bloomreach Discovery — carousel adapter.
 *
 * Lightweight complement to bp-bloomreach.js for homepage / editorial
 * product carousels (bp-product-carousel). Uses the same BR Category
 * Search API endpoint but targets the carousel's .bp-pc__track instead
 * of the collection grid.
 *
 * The Liquid markup (Shopify collection.products) stays in place as a
 * no-JS / SEO / dev-preview / CORS fallback. When BR is enabled and
 * reachable, this script replaces the product slides with BR-sourced
 * cards. On any fetch error the Shopify cards remain untouched.
 *
 * Activated by data-br-enabled="1" on <bp-product-carousel>.
 */

(function () {
  'use strict';

  // Default endpoint — production. Staging callers override via
  // cfg.host (set by `data-br-host` on the section root, populated
  // from `window.shop.br.host` in bp-shop-globals.liquid).
  const DEFAULT_ENDPOINT = 'https://core.dxpapi.com/api/v1/core/';
  const FIELD_LIST = 'pid,title,price,sale_price,thumb_image,url,colors';
  const MONEY = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

  function money(n) {
    if (n == null || isNaN(n)) return '';
    // Strip whole-dollar cents ($70.00 → $70), keep real cents ($70.50).
    return MONEY.format(Number(n)).replace(/\.00$/, '');
  }

  function esc(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g,  '&amp;')
      .replace(/</g,  '&lt;')
      .replace(/>/g,  '&gt;')
      .replace(/"/g,  '&quot;')
      .replace(/'/g,  '&#39;');
  }

  // BR `thumb_image` is a raw Shopify CDN URL with no resize; append
  // Shopify's `width` param so the CDN serves a card-sized image instead
  // of the ~250KB original. Shopify-CDN URLs only; other hosts untouched.
  function sizedImg(url, w) {
    if (!url || !/cdn\.shopify\.com|\/cdn\/shop\//.test(url)) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  function handleFromUrl(url) {
    const m = String(url || '').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : '';
  }

  // Shopify-style handleize: lowercase, non-alphanumeric runs -> '-', trim.
  function handleize(s) {
    return String(s == null ? '' : s)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // Display colour for this colorway card: the family colour whose
  // handleized form is the URL handle's suffix. Empty when none matches.
  function currentColor(url, colors) {
    const handle = handleFromUrl(url);
    if (!handle || !colors || !colors.length) return '';
    for (let i = 0; i < colors.length; i++) {
      const ch = handleize(colors[i]);
      if (ch && handle.length > ch.length + 1 && handle.slice(-(ch.length + 1)) === '-' + ch) {
        return colors[i];
      }
    }
    return '';
  }

  // Strip the trailing colourway off a title using the KNOWN colour,
  // case-insensitive. Separator is optional — BP titles append " - Color"
  // or plain " Color" — and only the colour anchors the match at end, so
  // internal words/hyphens survive (Atlas parity). No-op if it would
  // empty the name.
  function stripColorSuffix(title, color) {
    if (!title || !color) return title;
    const c = String(color).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp('\\s*[-\\u2013\\u2014]?\\s*' + c + '\\s*$', 'i');
    const out = String(title).replace(re, '').trim();
    return out || title;
  }

  function normalizeDoc(doc) {
    const handle  = handleFromUrl(doc.url);
    const sale    = doc.sale_price != null ? Number(doc.sale_price) : null;
    const list    = doc.price      != null ? Number(doc.price)      : null;
    // 0 / blank is NOT a real money value — never show "$0.00" or a bogus
    // sale (QA: Brooke, Hanky Panky). Validate before deciding sale state.
    const validSale = sale != null && !isNaN(sale) && sale > 0;
    const validList = list != null && !isNaN(list) && list > 0;
    const onSale  = validSale && validList && sale < list - 0.001;
    const priceOut = onSale ? sale : validList ? list : validSale ? sale : null;
    return {
      pid:              String(doc.pid || ''),
      // Prefer handle-based relative path — BR staging emits placeholder
      // `staging.example.com` hosts, prod emits `www.bostonproper.com`.
      // Relative paths always resolve to the visitor's current origin.
      url:              handle ? `/products/${handle}` : (doc.url || '#'),
      title:            String(doc.title || ''),
      image:            String(doc.thumb_image || ''),
      price:            priceOut,
      compare_at_price: onSale ? list : null,
      colors:           Array.isArray(doc.colors) ? [...new Set(doc.colors)] : [],
    };
  }

  const SWATCH_LIMIT = 8;

  function renderSwatches(colors) {
    if (!colors || !colors.length) return '';
    const shown    = colors.slice(0, SWATCH_LIMIT);
    const overflow = colors.length > SWATCH_LIMIT
      ? `<li class="bp-card__swatch-more" aria-hidden="true">+${colors.length - SWATCH_LIMIT}</li>`
      : '';
    const items = shown.map((c) => {
      const css = String(c).toLowerCase().replace(/[^a-z]/g, '');
      return `<li class="bp-card__swatch-item">
        <span class="bp-card__swatch-img"
              style="background-color:${esc(css)};"
              role="img"
              aria-label="${esc(c)}"
              title="${esc(c)}"></span>
      </li>`;
    }).join('');
    return `<ul class="bp-card__swatches" role="list" aria-label="Available colors">${items}${overflow}</ul>`;
  }

  function renderSlide(p) {
    // Only strike a real compare-at price strictly above the selling
    // price — never render a 0 / blank / ≤price compare (QA: Brooke).
    const cmp = Number(p.compare_at_price);
    const compare = p.compare_at_price != null && !isNaN(cmp) && cmp > Number(p.price)
      ? `<s class="bp-card__compare">${esc(money(cmp))}</s>`
      : '';
    const img = p.image
      ? `<img class="bp-card__image bp-card__image--primary"
              src="${esc(sizedImg(p.image, 540))}"
              srcset="${esc(sizedImg(p.image, 360))} 360w, ${esc(sizedImg(p.image, 540))} 540w"
              sizes="(max-width: 767px) 60vw, 300px"
              alt="${esc(p.title)}"
              data-bp-card-image-primary
              loading="lazy">`
      : '';
    return `<li class="bp-pc__slide">
      <product-card class="bp-card-host">
        <article class="bp-card"
                 data-product-id="${esc(p.pid)}"
                 data-product-url="${esc(p.url)}"
                 data-bp-card-qb-style="disabled"
                 data-bp-source="bloomreach">
          <div class="bp-card__media">
            <a class="bp-card__media-link" href="${esc(p.url)}" aria-label="${esc(p.title)}" ref="productCardLink">${img}</a>
            <div class="bp-card__badges" data-bp-card-badges></div>
          </div>
          <div class="bp-card__body">
            <h3 class="bp-card__title"><a href="${esc(p.url)}">${esc(stripColorSuffix(p.title, currentColor(p.url, p.colors)))}</a></h3>
            <div class="bp-card__price-row">
              <span class="bp-card__price">${esc(money(p.price))}</span>${compare}
            </div>
            ${renderSwatches(p.colors)}
          </div>
        </article>
      </product-card>
    </li>`;
  }

  function getBrUid() {
    // Empty (not a fabricated uid=0:v=app:ts=0:hc=1 sentinel) when the BR
    // cookie is absent — BR flags that sentinel as invalid. buildUrl strips
    // the empty param so anonymous calls simply omit _br_uid_2.
    const m = document.cookie.match(/(?:^|;\s*)_br_uid_2=([^;]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  }

  function buildUrl(cfg) {
    // Use search_type=category when a proper BR category ID is supplied
    // (e.g. cat000922 from the BR UI — set via the "BR category ID" field
    // in section settings). Fall back to keyword search with the collection
    // title when no BR ID is configured; keyword works even when the
    // category ID doesn't match BR's internal taxonomy.
    const hasBrCatId = cfg.useCategoryEndpoint;

    const p = new URLSearchParams({
      account_id:   cfg.accountId,
      domain_key:   cfg.domainKey,
      request_type: 'search',
      search_type:  hasBrCatId ? 'category' : 'keyword',
      q:            hasBrCatId ? cfg.catId : (cfg.catTitle || cfg.catId),
      rows:         String(cfg.rows),
      start:        '0',
      fl:           FIELD_LIST,
      // Pathname + query string — BR's "URL contains" audience rules (e.g.
      // utm_content-targeted ranking) match against this param; a bare
      // pathname strips the UTM params before BR ever sees them. See the
      // matching fix in bp-bloomreach.js's hydrateFromUrl().
      url:          cfg.siteUrl + window.location.pathname + window.location.search,
      ref_url:      document.referrer || window.location.href,
      _br_uid_2:    getBrUid(),
    });
    if (!p.get('_br_uid_2')) p.delete('_br_uid_2');
    if (cfg.viewId)  p.set('view_id',  cfg.viewId);
    // auth_key: BR's audit asks for it on all Discovery calls. Sent when
    // configured (cfg.authKey ← window.shop.br.authKey ← settings.bp_br_auth_key);
    // omitted while blank so we never send an empty value.
    if (cfg.authKey) p.set('auth_key', cfg.authKey);
    const host = cfg.host || (window.shop && window.shop.br && window.shop.br.host) || DEFAULT_ENDPOINT;
    return host + '?' + p.toString();
  }

  function init(root) {
    const catId = root.dataset.brCatId;
    if (!catId) return;

    const track = root.querySelector('.bp-pc__track');
    if (!track) return;

    const brGlobals = (window.shop && window.shop.br) || {};
    const cfg = {
      host:                root.dataset.brHost || brGlobals.host || DEFAULT_ENDPOINT,
      accountId:           root.dataset.brAccountId || brGlobals.accountId || '8261',
      domainKey:           root.dataset.brDomainKey || brGlobals.domainKey || 'bostonproper',
      authKey:             brGlobals.authKey || '',
      catId,
      catTitle:            root.dataset.brCatTitle || '',
      useCategoryEndpoint: root.dataset.brUseCategory === '1',
      rows:    parseInt(root.dataset.brRows || '10', 10),
      siteUrl: root.dataset.brSiteUrl || window.location.origin,
      viewId:  root.dataset.brViewId  || '',
    };

    // The intro tile is Liquid-rendered and not a product — keep it.
    const introSlide = track.querySelector('.bp-pc__slide--intro');

    fetch(buildUrl(cfg), { credentials: 'omit' })
      .then((res) => {
        if (!res.ok) throw new Error(`BR HTTP ${res.status}`);
        return res.json();
      })
      .then((json) => {
        const docs = (json.response && json.response.docs) || [];
        if (!docs.length) return; // nothing from BR — leave Shopify cards in place

        track.innerHTML = (introSlide ? introSlide.outerHTML : '')
          + docs.map(normalizeDoc).map(renderSlide).join('');

        // Refresh arrow disabled-state + scroll-indicator (scrollWidth changed).
        if (typeof root.updateArrows === 'function') {
          requestAnimationFrame(() => root.updateArrows());
        }

        // Best Seller / Trending badges — window.BpBadges loads globally
        // from theme.liquid (defer); short poll guards load order since
        // this fetch can resolve first.
        (function handOffToBadges(left) {
          if (window.BpBadges) { window.BpBadges.applyToCards(track); return; }
          if (left <= 0) return;
          setTimeout(() => handOffToBadges(left - 100), 100);
        })(3000);

        // Card hydration — same hand-off the PLP does (bp-bloomreach.js).
        // BR's feed carries no BP_Collection attribute, so the eyebrow
        // above the title can only come from the per-product hydrate
        // endpoint; without this the BR-rendered cards silently lose the
        // collection line that the Shopify-rendered ones show. Also
        // upgrades the provisional BR swatches to real sibling
        // URLs/images, exactly as on the PLP.
        (function handOffToHydrate(left) {
          if (window.BpCardHydrate) { window.BpCardHydrate.observeCards(track); return; }
          if (left <= 0) return;
          setTimeout(() => handOffToHydrate(left - 100), 100);
        })(3000);
      })
      .catch((err) => {
        // Fail-soft: Shopify-rendered cards remain untouched.
        console.error('[bp-bloomreach-carousel] fetch failed, keeping Shopify data:', err);
      });
  }

  document.querySelectorAll('bp-product-carousel[data-br-enabled]').forEach(init);
})();
